import {
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import {
  useTerminalDrawerActions,
  useTerminalDrawerState,
} from '#/features/terminal/drawer-context';
import type { TerminalTab } from '#/features/terminal/drawer-context';

import BlockTradeContextPump from '#/components/Terminal/BlockTradeContextPump';
import ChatView from '#/components/Terminal/ChatView';
import CronView from '#/components/Terminal/CronView';
import ExamplesView from '#/components/Terminal/ExamplesView';
import {
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
} from '#/components/Terminal/layout-constants';
import PendingMessageDrain from '#/components/Terminal/PendingMessageDrain';
import RecentsView from '#/components/Terminal/RecentsView';
import { SidebarResizeHandle } from '#/components/Terminal/SidebarResizeHandle';

export default function BlockTerminalTab() {
  return (
    <Container>
      <BlockTradeContextPump />
      <PendingMessageDrain />
      <Header />
      <Body />
    </Container>
  );
}

function Header() {
  const drawerState = useTerminalDrawerState();
  const drawerActions = useTerminalDrawerActions();
  return (
    <HeaderRow>
      <Title>DIME Terminal</Title>
      <Spacer />
      <HeaderControls>
        <SidebarToggleButton
          type="button"
          aria-label={
            drawerState.sidebarCollapsed ? 'Open sidebar' : 'Close sidebar'
          }
          title={
            drawerState.sidebarCollapsed ? 'Open sidebar' : 'Close sidebar'
          }
          onClick={drawerActions.toggleSidebar}
          $active={!drawerState.sidebarCollapsed}
        >
          <SidebarToggleIcon />
        </SidebarToggleButton>
        <PinSegment>
          <PinSegmentButton
            $active={drawerState.pinMode === 'bottom-tab'}
            onClick={() => drawerActions.setPinMode('bottom-tab')}
            type="button"
            title="Pin to bottom tab"
          >
            Bottom
          </PinSegmentButton>
          <PinSegmentButton
            $active={drawerState.pinMode === 'side-panel'}
            onClick={() => {
              drawerActions.setPinMode('side-panel');
              // Ensure drawer stays open when switching to side-panel
              if (!drawerState.isOpen) {
                drawerActions.open();
              }
            }}
            type="button"
            title="Pin as side panel"
          >
            Side
          </PinSegmentButton>
        </PinSegment>
      </HeaderControls>
    </HeaderRow>
  );
}

function Body() {
  const drawerState = useTerminalDrawerState();
  const drawerActions = useTerminalDrawerActions();

  // The "Recents" tab used to be one of the top-level tabs; the
  // sidebar replaces it. If a previous session persisted
  // `activeTab: 'recents'` in localStorage, redirect to chat so the
  // main pane always has something concrete to render.
  useEffect(() => {
    if (drawerState.activeTab === ('recents' as TerminalTab)) {
      drawerActions.setActiveTab('chat');
    }
  }, [drawerState.activeTab, drawerActions]);

  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
    currentWidth: number;
  } | null>(null);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startWidth: drawerState.sidebarWidth,
        currentWidth: drawerState.sidebarWidth,
      };
      setIsDragging(true);
    },
    [drawerState.sidebarWidth],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const r = dragRef.current;
      if (r == null || r.pointerId !== e.pointerId) return;
      const next = clamp(
        r.startWidth + (e.clientX - r.startX),
        SIDEBAR_MIN_WIDTH,
        SIDEBAR_MAX_WIDTH,
      );
      r.currentWidth = next;
      drawerActions.setSidebarWidth(next);
    },
    [drawerActions],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const r = dragRef.current;
      if (r == null || r.pointerId !== e.pointerId) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // already released — ignore
      }
      // Persist once at end of drag — pointer-move only wrote to
      // in-memory state to avoid 60-writes-per-second to localStorage.
      drawerActions.commitSidebarWidth(r.currentWidth);
      dragRef.current = null;
      setIsDragging(false);
    },
    [drawerActions],
  );

  const showResizeHandle = !drawerState.sidebarCollapsed;

  return (
    <BodyContainer>
      {!drawerState.sidebarCollapsed && (
        <Sidebar $width={drawerState.sidebarWidth}>
          <RecentsView />
        </Sidebar>
      )}
      {showResizeHandle && (
        <SidebarResizeHandle
          style={{ left: drawerState.sidebarWidth }}
          $dragging={isDragging}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      )}
      <MainPane>
        <TabPane $active={drawerState.activeTab === 'chat'}>
          <ChatView hideNewChatBar />
        </TabPane>
        <TabPane $active={drawerState.activeTab === 'examples'}>
          <ExamplesView />
        </TabPane>
        <TabPane $active={drawerState.activeTab === 'cron'}>
          <CronView />
        </TabPane>
      </MainPane>
    </BodyContainer>
  );
}

function SidebarToggleIcon() {
  // Bordered rectangle with a vertical divider — same glyph as the
  // Paradex `SidebarToggleIcon` so the action reads the same in both
  // apps.
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <rect
        x="3"
        y="4"
        width="18"
        height="16"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <line
        x1="9"
        y1="4"
        x2="9"
        y2="20"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}

const Container = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 8px;
  border-bottom: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
`;

const Title = styled.h2`
  margin: 0;
  padding: 0 4px;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1;
  color: #fff;
  letter-spacing: 0.02em;
`;

const Spacer = styled.div`
  flex: 1;
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SidebarToggleButton = styled.button<{ $active: boolean }>`
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  color: ${(p) => (p.$active ? '#fff' : 'var(--text-secondary)')};
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    color: #fff;
  }
`;

const PinSegment = styled.div`
  display: flex;
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  border-radius: 6px;
  overflow: hidden;
`;

const PinSegmentButton = styled.button<{ $active: boolean }>`
  padding: 3px 10px;
  font-size: 0.6875rem;
  font-weight: 500;
  background: ${(p) =>
    p.$active ? 'var(--hover-bg, rgba(255, 255, 255, 0.08))' : 'transparent'};
  color: ${(p) =>
    p.$active ? 'var(--text-primary)' : 'var(--text-secondary)'};
  border: none;
  cursor: pointer;
  transition: background 0.15s ease;

  & + & {
    border-left: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  }

  &:hover {
    color: var(--text-primary);
  }
`;

const BodyContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  position: relative;
`;

const Sidebar = styled.div<{ readonly $width: number }>`
  flex-shrink: 0;
  width: ${({ $width }) => $width}px;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
`;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

const MainPane = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const TabPane = styled.div<{ $active: boolean }>`
  display: ${(p) => (p.$active ? 'flex' : 'none')};
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;

// Re-export drawer types so other components can flag-gate without an
// extra import.
export type { PinMode, TerminalTab } from '#/features/terminal/drawer-context';
